/**
 * Unit and range formatting (PRD §9.5).
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.5, §4.3
 */
import { EN_DASH, NBSP } from './constants';
import type { Precision } from './constants';
import { formatNumber, formatSignificant, isFormattable } from './number';

/**
 * A value with its unit, separated by a non-breaking space (PRD §9.5).
 *
 * Non-breaking because `20` at the end of one line and `MW` at the start of the
 * next is unreadable, and in a narrow table column it happens often.
 */
export function formatUnit(
  value: number,
  unit: string,
  precision: Precision = 'table',
): string {
  if (!isFormattable(value)) return '';
  return `${formatSignificant(value, precision)}${NBSP}${unit}`;
}

/** Power in megawatts. */
export function formatPower(mw: number, precision: Precision = 'table'): string {
  return formatUnit(mw, 'MW', precision);
}

/** Energy in megawatt-hours. */
export function formatEnergy(mwh: number, precision: Precision = 'table'): string {
  return formatUnit(mwh, 'MWh', precision);
}

/** Temperature. The degree sign takes a space, per PRD §9.5's `400 °C`. */
export function formatTemperature(
  celsius: number,
  precision: Precision = 'table',
): string {
  return formatUnit(celsius, '°C', precision);
}

/**
 * Storage capacity.
 *
 * PRD §9.5: **storage is always both** power and energy — `20 MW / 40 MWh`.
 * A storage asset described by power alone is ambiguous about how long it can
 * sustain that power, which is the number that decides whether a pilot is
 * viable. When only one figure is known, that one is shown rather than
 * inventing the other.
 */
export function formatCapacity(
  capacity: { mw?: number | null | undefined; mwh?: number | null | undefined },
  precision: Precision = 'table',
): string {
  /* Destructured first so `isFormattable` narrows the values themselves.
     Storing the checks in booleans loses the narrowing, which is what forced
     a non-null assertion at every use. */
  const { mw, mwh } = capacity;

  if (isFormattable(mw) && isFormattable(mwh)) {
    return `${formatPower(mw, precision)} / ${formatEnergy(mwh, precision)}`;
  }

  if (isFormattable(mw)) return formatPower(mw, precision);
  if (isFormattable(mwh)) return formatEnergy(mwh, precision);

  return '';
}

/**
 * A range, joined by an en dash (PRD §9.5: `2026–2030`).
 *
 * No spaces around the dash — that is the typographic convention for a closed
 * numeric range, and the PRD's own example shows it that way.
 */
export function formatRange(from: number, to: number, unit?: string): string {
  if (!isFormattable(from) || !isFormattable(to)) return '';

  const body = `${formatNumber(from)}${EN_DASH}${formatNumber(to)}`;
  return unit === undefined ? body : `${body}${NBSP}${unit}`;
}

/** A year range: `2026–2030`, never grouped as `2,026`. */
export function formatYearRange(from: number, to: number): string {
  if (!isFormattable(from) || !isFormattable(to)) return '';
  return `${String(Math.trunc(from))}${EN_DASH}${String(Math.trunc(to))}`;
}

/**
 * Binary size steps above a kilobyte.
 *
 * Bytes are never shown — a raw count means nothing to a reader — so `KB` is
 * the starting unit and this lists what comes after it.
 */
const SIZE_UNITS = ['MB', 'GB'] as const;

type FileSizeUnit = 'KB' | (typeof SIZE_UNITS)[number];

/**
 * A file size, in the units a person reads.
 *
 * Binary (1024), matching what every operating system reports, so a file the
 * user's file manager calls 1.2 MB is not shown here as 1.3 MB.
 *
 * One decimal place below 10 and none above: `1.2 MB` is useful, `1.2 KB` is
 * not more useful than `1 KB`, and `24.6 MB` next to a 25 MB cap tells the
 * reader they are close in a way `25 MB` would not. Anything under a kilobyte
 * reads as `1 KB` rather than a byte count — the exact figure is noise, and
 * `0 KB` would look like a failed upload.
 */
export function formatFileSize(bytes: number): string {
  if (!isFormattable(bytes) || bytes <= 0) return `1${NBSP}KB`;

  /* Anything under half a kilobyte reports as `1 KB`. A 300-byte file is
     0.29 KB, which rounds to `0 KB` and reads as a failed upload — and the
     exact figure is noise at that size. Half a kilobyte and up keeps its
     decimal, so `0.5 KB` is still shown honestly. */
  if (bytes < 512) return `1${NBSP}KB`;

  /* Stepped over the unit names rather than an index into them. Indexing
     would need either a `!` (forbidden) or a `?? 'KB'` fallback that no test
     could reach — iterating carries the current unit in a variable instead,
     so there is nothing to assert on. */
  let size = bytes / 1024;
  let unit: FileSizeUnit = 'KB';

  for (const next of SIZE_UNITS) {
    if (size < 1024) break;
    size /= 1024;
    unit = next;
  }

  const rounded = formatNumber(size, {
    maximumFractionDigits: size < 10 ? 1 : 0,
  });

  return `${rounded}${NBSP}${unit}`;
}
